import * as Immutable from 'immutable';
import * as React from 'react';
import { TableProps } from 'react-table';
import { v4 as uuid } from 'uuid';

import { addModifiers } from '../../../shared/helpers';
import './Block.scss';
import {
  ColumnSort,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { sortMethod } from './utils/sorting';
import { getFooterGroups } from '../../../shared/table/table-helpers';

//type RowData = string[];
type ConvertedRowData = Record<string, string>;
interface BlockTableProps {
  data: Immutable.List<Immutable.List<string>>;
  firstRowIsTableHeader: boolean;
  firstColIsHeader: boolean;
}

interface BlockTableLegacyProps {
  data: Immutable.List<Immutable.List<string>>;
  firstColIsHeader: boolean;
}

export interface BlockTableReactTableProps {
  maxHeight?: number | string;
  columns: TableOptions<ConvertedRowData>['columns'];
  data: TableOptions<ConvertedRowData>['data'];
  pageSize?: number;
}

const BlockTableLegacy = ({ data, firstColIsHeader }: BlockTableLegacyProps) => {
  const firstColIsTableHeader = firstColIsHeader ? ['firstColIsHeader'] : [];

  return (
    <div className={addModifiers('block-table-wrapper', ['legacy'])}>
      <table cellSpacing="0" className={addModifiers('block-table', firstColIsTableHeader)}>
        <tbody>
          {data.map((row: Immutable.List<string>, key: number) => (
            <tr key={key} className="block-table__row">
              {row.map((value: string, index: number) => (
                <td className={addModifiers('block-table__el', firstColIsHeader && index === 0 ? ['header-col'] : [])} key={index}>
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const columnHelper = createColumnHelper<ConvertedRowData>();

const getColumns = (headers: Immutable.List<string>) =>
  headers.toJS().map((header, index) =>
    // @ts-ignore
    columnHelper.accessor(`col-${index}`, {
      id: `col-${index}`,
      header: () => header,
      // @ts-ignore
      sortingFn: 'sortParsedValues',
    })
  );

const getData = (data: Immutable.List<Immutable.List<string>>): TableProps['data'] =>
  data.toArray().map((row: Immutable.List<string>) =>
    row.toArray().reduce<Record<string, string>>((prev, cell, index) => {
      prev[`col-${index}`] = cell;

      return prev;
    }, {})
  );

export const BlockTableReactTable = ({ columns, data, maxHeight: bodyHeight }: BlockTableReactTableProps) => {
  // ReactTable's ref holds ReactTable component instead of DOM of the wrapped element
  // Assign it a unique ID to query its DOM to calculate for scrollbar
  const tableId = React.useMemo(() => uuid(), []);
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    columns: columns,
    data: data,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    sortingFns: {
      // @ts-ignore
      sortParsedValues: (rowA: ConvertedRowData, rowB: ConvertedRowData, columnId: string) => {
        // @ts-ignore
        if (rowA.original[columnId]?.[0]?.nodeValue) {
          // @ts-ignore
          const valA = rowA.original[columnId]?.[0]?.nodeValue;
          // @ts-ignore
          const valB = rowB.original[columnId]?.[0]?.nodeValue;
          return sortMethod(valA, valB, true);
        }
        // @ts-ignore
        return sortMethod(rowA.original[columnId], rowB.original[columnId]);
      },
    },
  });

  const headerGroups = table.getHeaderGroups();
  return (
    <div className="block-table-container">
      <div id={tableId}>
        <div className="ReactTable block-table-wrapper">
          <div className="rt-table block-table" role="grid">
            {headerGroups.map((headerGroup, i) => (
              <div key={headerGroup.id} className={headerGroups.length === 2 && i === 0 ? 'rt-thead -headerGroups' : 'rt-thead -header'}>
                <div
                  {...{
                    key: headerGroup.id,
                    className: 'rt-tr',
                    role: 'row',
                  }}
                >
                  {headerGroup.headers.map(header => (
                    // eslint-disable-next-line react/jsx-key
                    <div
                      {...{
                        key: header.id,
                        className: getHeaderClassName(header, sorting),
                        onClick: header.column.getToggleSortingHandler(),
                        role: 'columnheader',
                        style: {
                          width: header.getSize(),
                          flex: `${header.getSize()} 0 auto`,
                        },
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div
              {...{
                className: 'rt-tbody',
              }}
            >
              {table.getRowModel().rows.map((row, i) => (
                <div className="rt-tr-group" role="rowgroup" key={row.id}>
                  <div
                    {...{
                      key: row.id,
                      className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                      role: 'row',
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      // eslint-disable-next-line react/jsx-key
                      <div
                        {...{
                          key: cell.id,
                          // @ts-ignore
                          className: `rt-td ${cell.column.columnDef.meta?.className ?? ''}`,
                          role: 'gridcell',
                          style: {
                            flex: `${cell.column.getSize()} 0 auto`,
                            width: cell.column.getSize(),
                          },
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {getFooterGroups(table)}
          </div>
        </div>
      </div>
    </div>
  );
};

export function getHeaderClassName<DataType>(header: Header<DataType, any>, sorting?: ColumnSort[]) {
  let className = 'rt-th';
  if (header.column.getCanSort()) {
    const columnSort = sorting?.[0];
    if (columnSort?.id === header.id) {
      className += columnSort?.desc ? ' -sort-desc' : ' -sort-asc';
    }
    className += ' -cursor-pointer';
  }
  return className;
}

export const BlockTable = ({ data, firstRowIsTableHeader, firstColIsHeader }: BlockTableProps) =>
  firstRowIsTableHeader ? (
    <BlockTableReactTable columns={getColumns(data.get(0) as any)} data={getData(data.delete(0))} />
  ) : (
    <BlockTableLegacy data={data} firstColIsHeader={firstColIsHeader} />
  );
